import classNames from 'classnames';
import type { ComponentProps, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import logSignUpScreenContinueButtonTapped from '@alltrails/amplitude/events/logSignUpScreenContinueButtonTapped';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import Logo from '@alltrails/denali/components/Logo';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import AppPromotion from '@alltrails/install-prompt/components/AppPromotion';
import { useModalAriaLabelledBy } from '@alltrails/modal/components/Modal/ModalAriaLabelledByContext';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import AltLoginsForm from '../AltLoginsForm';
import altLoginStyles from '../AltLoginsForm/AltLoginsForm.module.scss';
import GoogleLoginButton from '../GoogleLoginButton';
import SectionDivider from '../SectionDivider';
import TermsOfService from '../TermsOfService';
import ContinueInAppButton from './ContinueInAppButton';
import mobileStyles from './InitialMobile.module.scss';
import signupModalStyles from './SignUpModal.module.scss';

type Props = Pick<ComponentProps<typeof AltLoginsForm>, 'googleLoginHandler'> & {
  deeplinkData?: DeeplinkData;
  displayTrigger: CarouselDisplayTrigger;
  heading?: ReactNode;
  hideAppPromotion?: boolean;
  onContinueInAppClick?: () => void;
  onMoreOptionsClick: () => void;
};

export default function InitialSimplified({
  deeplinkData,
  displayTrigger,
  googleLoginHandler,
  heading,
  hideAppPromotion = false,
  onContinueInAppClick,
  onMoreOptionsClick
}: Props) {
  const isMobile = useIsMobileSizedScreen();
  const ariaLabelledBy = useModalAriaLabelledBy();

  const googleButton = <GoogleLoginButton googleLoginHandler={googleLoginHandler} flatColors />;

  if (isMobile) {
    return (
      <div className={mobileStyles.content}>
        {hideAppPromotion ? (
          <Heading as="h4" id={ariaLabelledBy} size="400">
            {heading}
          </Heading>
        ) : (
          <AppPromotion heading={heading} />
        )}
        <div>
          {!hideAppPromotion && (
            <>
              <ContinueInAppButton deeplinkData={deeplinkData} displayTrigger={displayTrigger} flatColors />
              <SectionDivider classes={signupModalStyles.divider}>
                <FormattedMessage defaultMessage="or" />
              </SectionDivider>
            </>
          )}
          <div className={altLoginStyles.content}>{googleButton}</div>
          <Button
            className={signupModalStyles.email}
            onClick={onMoreOptionsClick}
            text={<FormattedMessage defaultMessage="More options" />}
            width="full"
          />
        </div>
        <div className={mobileStyles.already}>
          <TermsOfService />
        </div>
      </div>
    );
  }

  return (
    <div className={signupModalStyles.content}>
      <div className={signupModalStyles.logo}>
        <Logo color="monochrome-dark" size="lg" variant="symbol" />
      </div>
      <Heading as="h4" className={classNames(signupModalStyles.heading, signupModalStyles.headingMaxWidth)} id={ariaLabelledBy} size="400">
        {heading || <FormattedMessage defaultMessage="To keep exploring trails, log in or sign up for free" />}
      </Heading>
      <div className={altLoginStyles.content}>{googleButton}</div>
      <div className={signupModalStyles.signUpLogInContainer}>
        <Button
          className={signupModalStyles.email}
          onClick={onMoreOptionsClick}
          text={<FormattedMessage defaultMessage="More options" />}
          variant="secondary"
          width="full"
        />
        <SectionDivider classes={signupModalStyles.divider}>
          <FormattedMessage defaultMessage="or" />
        </SectionDivider>
        <Button
          onClick={() => {
            logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'continue in app' });
            onContinueInAppClick?.();
          }}
          text={<FormattedMessage defaultMessage="Continue in app" />}
          variant="primary"
          width="full"
        />
      </div>
      <TermsOfService />
    </div>
  );
}
